import { FormEvent, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import type { Tag, TagCategory } from '@/lib/types'

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export default function TagsPage() {
  const [tags, setTags] = useState<Tag[]>([])
  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [category, setCategory] = useState<TagCategory>('SPECIALTY')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    apiFetch<Tag[]>('/tags').then(setTags).catch((e) => setError(e.message))
  }, [])

  useEffect(load, [load])

  const create = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await apiFetch('/tags', {
        method: 'POST',
        body: JSON.stringify({ name, slug: slugify(name), category, description }),
      })
      setName('')
      setDescription('')
      load()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const remove = async (tag: Tag) => {
    setError('')
    try {
      await apiFetch(`/tags/${tag.id}`, { method: 'DELETE' })
      load()
    } catch (err: any) {
      setError(err.message)
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <Link to="/studio" className="spec-label hover:text-foreground">← Inventory</Link>
      <h1 className="mt-3 font-display text-3xl font-bold">Tags</h1>
      <p className="mt-2 text-muted-foreground">
        Tags are how warm-ups get configured. CORE tags form the everyday warm-up; SPECIALTY
        tags handle particular situations.
      </p>

      {error && <p className="mt-4 text-destructive">{error}</p>}

      <form onSubmit={create} className="mt-8 grid gap-3 rounded-lg border bg-card p-4 sm:grid-cols-[1fr_auto_auto]">
        <input
          required
          placeholder="Tag name (e.g. Sirens)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-md border bg-background px-3 py-2"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as TagCategory)}
          className="rounded-md border bg-background px-2 py-2 text-sm"
        >
          <option value="CORE">Core</option>
          <option value="SPECIALTY">Specialty</option>
        </select>
        <button
          type="submit"
          disabled={busy}
          className="min-h-[44px] rounded-full bg-primary px-6 font-medium text-primary-foreground disabled:opacity-60"
        >
          Add tag
        </button>
        <input
          placeholder="Description (optional, shows as a tooltip)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="rounded-md border bg-background px-3 py-2 sm:col-span-3"
        />
      </form>

      <div className="mt-8 space-y-2">
        {tags.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3">
            <div>
              <p className="font-medium">
                {t.name}{' '}
                <span className="spec-label ml-1">{t.category}</span>
                {typeof t.warmupCount === 'number' && (
                  <span className="ml-2 text-xs text-muted-foreground">{t.warmupCount} exercise{t.warmupCount === 1 ? '' : 's'}</span>
                )}
              </p>
              {t.description && <p className="text-sm text-muted-foreground">{t.description}</p>}
            </div>
            <button
              onClick={() => remove(t)}
              className="min-h-[44px] shrink-0 rounded-full border border-destructive px-4 text-sm font-medium text-destructive"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
